
// src/services/sharePointService.ts
import { spfi, SPFx } from "@pnp/sp";
import { LogLevel, PnPLogging } from "@pnp/logging";
import { GraphFI, SPFx as GraphSPFx } from "@pnp/graph";
import "@pnp/sp/webs";
import "@pnp/sp/lists";
import "@pnp/sp/items";
import "@pnp/graph/users";
import "@pnp/graph/groups";
import LoggingService from "./LoggingService";
import { getMsalToken } from "../msalAuth";

// The central SharePoint service class
class SharePointService {
  private sp: any = null;
  private graph: any = null;
  private siteUrl: string = "";
  private isInitialized: boolean = false;
  
  // Initialize the SharePoint service with site URL
  async initialize(siteUrl: string): Promise<void> {
    if (!siteUrl) {
      LoggingService.warn("No site URL provided for SharePoint initialization", {}, "SharePointService");
      return;
    }
    
    this.siteUrl = siteUrl;
    
    try {
      LoggingService.info("Initializing SharePoint service", { siteUrl }, "SharePointService");
      
      // Get token
      const token = await getMsalToken();
      
      if (!token) {
        throw new Error("Failed to acquire token");
      }
      
      // Setup PnP with MSAL token - use numeric value for LogLevel to avoid issues with const enums
      // Warning is 1 in LogLevel enum
      this.sp = spfi(siteUrl).using(PnPLogging(1));
      this.graph = new GraphFI().using(PnPLogging(1));
      
      this.isInitialized = true;
      LoggingService.info("SharePoint service initialized", { siteUrl }, "SharePointService");
    } catch (error) {
      LoggingService.error("Error initializing SharePoint service", error, "SharePointService");
      this.isInitialized = false;
      throw error;
    }
  }
  
  // Test the connection to SharePoint
  async testConnection(): Promise<boolean> {
    try {
      if (!this.isInitialized) {
        LoggingService.warn("SharePoint service not initialized", {}, "SharePointService");
        return false;
      }
      
      LoggingService.info("Testing SharePoint connection", {}, "SharePointService");
      
      // Try to get web info as a connection test
      const webInfo = await this.getWebInfo();
      
      if (webInfo && webInfo.Title) {
        LoggingService.info("Connection test successful", { webTitle: webInfo.Title }, "SharePointService");
        return true;
      }
      
      return false;
    } catch (error) {
      LoggingService.error("Connection test failed", error, "SharePointService");
      return false;
    }
  }
  
  // Get information about the current SharePoint web
  async getWebInfo(): Promise<any> {
    if (!this.isInitialized) {
      LoggingService.warn("SharePoint service not initialized", {}, "SharePointService");
      throw new Error("SharePoint service not initialized");
    }
    
    try {
      LoggingService.info("Getting web info", {}, "SharePointService");
      const webInfo = await this.sp.web();
      return webInfo;
    } catch (error) {
      LoggingService.error("Error getting web info", error, "SharePointService");
      throw error;
    }
  }
  
  // Get all lists in the current web
  async getLists(): Promise<any[]> {
    if (!this.isInitialized) {
      LoggingService.warn("SharePoint service not initialized", {}, "SharePointService");
      throw new Error("SharePoint service not initialized");
    }
    
    try {
      LoggingService.info("Getting lists", {}, "SharePointService");
      const lists = await this.sp.web.lists();
      return lists;
    } catch (error) {
      LoggingService.error("Error getting lists", error, "SharePointService");
      throw error;
    }
  }
  
  // Initialize with SPFx context (for SharePoint-hosted scenarios)
  initializeWithSPFxContext(context: any): void {
    if (!context) {
      LoggingService.warn("No SPFx context provided", {}, "SharePointService");
      return;
    }
    
    try {
      LoggingService.info("Initializing SharePoint service with SPFx context", {}, "SharePointService");
      
      this.sp = spfi().using(SPFx(context));
      this.graph = new GraphFI().using(GraphSPFx(context));
      
      this.siteUrl = context.pageContext.web.absoluteUrl;
      this.isInitialized = true;
      
      LoggingService.info("SharePoint service initialized with SPFx context", {
        siteUrl: this.siteUrl
      }, "SharePointService");
    } catch (error) {
      LoggingService.error("Error initializing SharePoint service with SPFx context", error, "SharePointService");
      this.isInitialized = false;
    }
  }
}

// Create and export a singleton instance
const sharePointService = new SharePointService();

// Also export individual methods for easier mocking and testing
export const initialize = sharePointService.initialize.bind(sharePointService);
export const testConnection = sharePointService.testConnection.bind(sharePointService);
export const getWebInfo = sharePointService.getWebInfo.bind(sharePointService);
export const getLists = sharePointService.getLists.bind(sharePointService);

export default sharePointService;
